import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./AccountingPurchasesPage.css";

type PurchaseDocument = {
  id: string;
  supplier_id: string | null;
  supplier_name: string;
  supplier_document_number: string | null;
  document_type: string;
  series: string;
  document_number: string;
  issue_date: string;
  due_date: string | null;
  currency_code: string;
  taxable_amount: number | string;
  tax_total: number | string;
  total: number | string;
  status: string;
  sunat_status: string;
  payable_id: string | null;
  payable_status: string | null;
  payable_balance: number | string | null;
  updated_at: string;
};

function money(value: number | string | null, currency: string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

export function AccountingPurchasesPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [rows, setRows] = useState<PurchaseDocument[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dueDates, setDueDates] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "list_accounting_purchase_documents_ui",
      {
        p_organization_id: organizationId,
        p_search: search.trim() || null,
      },
    );

    if (rpcError) {
      setRows([]);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    const next = (data ?? []) as PurchaseDocument[];
    setRows(next);

    setDueDates((current) => {
      const copy = { ...current };
      for (const row of next) {
        if (!copy[row.id]) {
          copy[row.id] = row.due_date ?? "";
        }
      }
      return copy;
    });

    setLoading(false);
  }, [organizationId, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 220);
    return () => window.clearTimeout(timer);
  }, [load]);

  async function setDueDate(event: FormEvent, row: PurchaseDocument) {
    event.preventDefault();

    const dueDate = dueDates[row.id];

    if (!dueDate) {
      setError("Define una fecha de vencimiento.");
      return;
    }

    setBusyId(row.id);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "set_accounting_purchase_credit_due_date",
      {
        p_purchase_document_id: row.id,
        p_due_date: dueDate,
      },
    );

    setBusyId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Vencimiento de crédito registrado.");
    await load();
  }

  async function createPayable(row: PurchaseDocument) {
    setBusyId(row.id);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "ensure_ap_payable_for_purchase_document",
      {
        p_purchase_document_id: row.id,
      },
    );

    setBusyId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Cuenta por pagar creada.");
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Contabilidad · Compras</span>
          <h1>Comprobantes recibidos</h1>
          <p>
            Revisa compras registradas y crea cuentas por pagar solo cuando la
            operación sea a crédito.
          </p>
        </div>

        <Link className="ghost-button accounting-purchase-back" to="/contabilidad">
          Volver
        </Link>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? (
        <div className="form-success accounting-purchase-message">
          {success}
        </div>
      ) : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : rows.length}</strong>
          <span> comprobantes</span>
        </div>

        <input
          type="search"
          placeholder="Buscar proveedor, RUC o comprobante"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {!loading && rows.length === 0 && !error ? (
        <section className="empty-state">
          <strong>No hay comprobantes de compra registrados.</strong>
        </section>
      ) : null}

      <section className="accounting-purchase-grid">
        {rows.map((row) => (
          <article className="accounting-purchase-card" key={row.id}>
            <div className="accounting-purchase-heading">
              <div>
                <span className="status-badge">{row.status}</span>
                <h2>
                  {row.series}-{row.document_number}
                </h2>
                <p>{row.supplier_name}</p>
              </div>
              <strong>{money(row.total, row.currency_code)}</strong>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Emisión</dt>
                <dd>{row.issue_date}</dd>
              </div>
              <div>
                <dt>Vencimiento</dt>
                <dd>{row.due_date ?? "Contado / no definido"}</dd>
              </div>
              <div>
                <dt>Base</dt>
                <dd>{money(row.taxable_amount, row.currency_code)}</dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{money(row.tax_total, row.currency_code)}</dd>
              </div>
            </dl>

            {row.payable_id ? (
              <div className="accounting-purchase-payable">
                <span>CxP {row.payable_status ?? "open"}</span>
                <strong>
                  Saldo {money(row.payable_balance, row.currency_code)}
                </strong>
              </div>
            ) : row.due_date ? (
              <div className="quotation-actions">
                <button
                  className="compact-button"
                  type="button"
                  disabled={busyId === row.id}
                  onClick={() => void createPayable(row)}
                >
                  Crear CxP
                </button>
              </div>
            ) : (
              <form
                className="accounting-purchase-credit-form"
                onSubmit={(event) => void setDueDate(event, row)}
              >
                <label>
                  Convertir a crédito · vencimiento
                  <input
                    type="date"
                    min={row.issue_date}
                    value={dueDates[row.id] ?? ""}
                    onChange={(event) =>
                      setDueDates((current) => ({
                        ...current,
                        [row.id]: event.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  className="compact-button"
                  type="submit"
                  disabled={busyId === row.id}
                >
                  Definir crédito
                </button>
              </form>
            )}

            <div className="accounting-sunat-state">
              <span>Estado fiscal</span>
              <strong>{row.sunat_status}</strong>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
